// The session-history rail — this project's earlier chats, in the Focus drawer.
//
// Data comes from `useSessions`, the same hook the dashboard's SessionSidebar
// uses; the presentation does not, because `.loom-focus` is a fixed flex column
// with its own token set and "pick" means something different here (adopt the
// per-project CHAT binding, not navigate to a session-bearing URL — Focus has
// none). The rail is also the ONE caller that asks for `resumableOnly` and for
// `kind=chat`: every row here is a "reopen this" button, so a row the server
// could not open is a lie, and reopening an issue conversation is withheld —
// the landing for one is buggy, so the honest list is chats. The dashboard
// surfaces keep the unfiltered list.
//
// Read-only by design: renaming is dead weight without deleting, and deleting
// stamps the write-once `endedAt` latch that nothing ever clears. That is not a
// button that belongs one hover away in a browsing surface.
//
// The drawer's head and its close button belong to `FocusFrame` (the `<aside>`
// in ui.tsx), so they survive every state below — including the refusal panel,
// which takes the whole list over.

import { useEffect, useRef, useState, type ReactNode } from "react";

import { useFormatDate, useRelativeTime } from "../i18n/format";
import { useT } from "../i18n/index.js";
import { readProjectSessionId } from "../dashboard/useProjectSession";
import { dayKeyOf, groupByDay } from "../lib/group-by-day";
import { signInAgain } from "../lib/host";
import { useSessions, type SessionIndexEntry } from "../projects/useSessions";

import type { PickRefusal } from "./useFocusSessionPick";

/**
 * `GET /sessions`' own ceiling, and the whole of pagination: `ListQuerySchema`
 * carries no offset and no cursor, so there is no page two to build. With the
 * index horizon bounded at 30 days the list is bounded by retention instead.
 */
const RAIL_LIMIT = 100;

/**
 * The placeholder `initialTitle` stamps on a row at `POST /sessions` time,
 * mirroring `apps/server/src/sessions-index/title.ts`. A row still wearing it
 * never bound a flow and never took a user turn, so there is nothing in it to
 * name. No lookbehind — Safari 14 throws on one at parse time.
 */
const PLACEHOLDER_TITLE = /^Session @ \d{2}:\d{2}$/;

/**
 * Module constants, not inline literals: `useFormatDate` lists `options` in its
 * `useCallback` deps, so a fresh object per render re-mints the formatter on
 * every commit.
 */
const ROW_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  dateStyle: "medium",
  timeStyle: "short",
};
const GROUP_DATE_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: "medium" };

/**
 * How often the drawer re-renders so its relative timestamps stay true. The
 * smallest unit `useRelativeTime` ever prints is a minute, so ticking faster
 * buys nothing; ticking slower lets a visible label be wrong.
 */
const RELATIVE_CLOCK_TICK_MS = 60_000;

export function FocusSessionRail({
  projectId,
  chatStageActive,
  onPick,
  onNewChat,
  pendingSessionId,
  refusal,
  refusedSessionId,
  onDismissRefusal,
  onRetry,
}: {
  readonly projectId: number;
  /**
   * The issue the user is standing in, when the surface behind the drawer is a
   * refine URL. That conversation is not on this list — the disclosure above the
   * rows names it, so its absence reads as scope rather than as loss.
   */
  /**
   * Whether the surface behind the drawer IS the chat. Only then is the bound
   * chat the conversation the user is standing in; on every other Focus surface
   * it is one they left. The pick's "already open" guard reads the same flag, so
   * the badge and the swallowed click can never disagree.
   */
  readonly chatStageActive: boolean;
  readonly onPick: (entry: SessionIndexEntry) => void;
  /**
   * Start a NEW chat. The rail is the one surface that can offer this from
   * anywhere — the wizard stages, a refine URL, or a chat — because it is the
   * only Focus chrome that outlives the stage. Before it, the act existed only
   * INSIDE a chat that had already gone wrong (the budget wall, the ended
   * block), so a healthy tab could never produce a second row.
   *
   * A create, not a write to an existing row: the read-only rule this file
   * opens with is about DELETE stamping the write-once `endedAt` latch, and
   * nothing here touches that. It does cost a GitLab pipeline, which is why the
   * disclosure sits on the control.
   */
  readonly onNewChat: () => void;
  /** The row whose pick is resolving; the whole list is inert while it is set. */
  readonly pendingSessionId: string | null;
  /** How the last pick was refused, or null. Four of the five are terminal. */
  readonly refusal: PickRefusal | null;
  /** The row that refusal was about. */
  readonly refusedSessionId: string | null;
  readonly onDismissRefusal: () => void;
  readonly onRetry: () => void;
}) {
  const t = useT();
  const relative = useRelativeTime();
  // The rows are a SNAPSHOT — one fetch on open, no poll and no push (the cost
  // notice hedges with "usually" for exactly that reason). The badges being
  // stale under the pointer is a deliberate, documented trade; the CLOCK being
  // stale is not. `relative()` reads `Date.now()` at render, so a drawer left
  // open held "4 minutes ago" for as long as the user lingered — a time that is
  // silently wrong with nothing on screen saying so. One tick a minute is
  // enough for a label whose smallest unit IS a minute, and it re-renders only
  // the drawer, only while it is mounted.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => {
      setClockTick((n) => n + 1);
    }, RELATIVE_CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);
  const absolute = useFormatDate(ROW_DATE_FORMAT);
  const groupDate = useFormatDate(GROUP_DATE_FORMAT);
  const { state, reload } = useSessions({
    projectId,
    limit: RAIL_LIMIT,
    resumableOnly: true,
    kind: "chat",
  });
  // The server's filter is the real one — it runs in the same WHERE as the
  // limit, so "100 rows" means 100 chats. This is its fail-closed twin for the
  // ROLLING-DEPLOY window: `ListQuerySchema` is a non-strict zod object, so a
  // server that predates the param drops it silently and answers with
  // everything. Junk in the param is not what this covers — that is a 400 at the
  // route.
  const chatSessions =
    state.kind === "ready"
      ? state.sessions.filter((e) => e.kind === "chat")
      : [];
  // The rail mounts when the drawer OPENS, so a mount-time read is a fresh read
  // — which is all a "this one is open now" badge needs, and it keeps the badge
  // stable while a pick is resolving underneath it.
  //
  // The chat binding is "the conversation you are standing in" only while the
  // surface behind the drawer IS the chat — a fact about the SURFACE, which no
  // URL carries: the wizard stages share the project URL with the chat while
  // standing in the issue slot. Anywhere else the bound chat is a conversation
  // the user left, and badging it "open now" both claims they are in it and
  // makes the pick's matching guard swallow the click that goes back to it.
  const [activeSessionId] = useState(() =>
    chatStageActive ? readProjectSessionId(projectId, "chat") : null,
  );
  // Rows the server refused, and WHY. The list is a snapshot taken at drawer-open
  // and there is no re-fetch behind a pick, so a row that just 404'd (or came
  // back revoked, or came back an issue conversation) would otherwise keep
  // offering itself — and keep wearing whatever badges the snapshot gave it.
  // All THREE terminal refusals are recorded, not just `gone`: leaving the other
  // two out keeps the contradiction on a different row class.
  // Scoped to this opening, like every other notice here.
  const [unopenableRows, setUnopenableRows] = useState<
    ReadonlyMap<string, "gone" | "ended" | "issue">
  >(() => new Map());
  useEffect(() => {
    if (refusedSessionId === null) return;
    const state =
      refusal === "gone"
        ? "gone"
        : refusal === "revoked"
          ? "ended"
          : refusal === "issueOnly"
            ? "issue"
            : null;
    if (state === null) return;
    setUnopenableRows((prev) =>
      prev.get(refusedSessionId) === state
        ? prev
        : new Map([...prev, [refusedSessionId, state]]),
    );
  }, [refusal, refusedSessionId]);

  // The refusal notice is scoped to THIS opening of the drawer. The rail
  // unmounts when the drawer closes while `refusal` lives on the host, so
  // without this it outlives the panel that explains it: reopening the drawer
  // would show a terminal refusal with no list behind it, or a "couldn't open
  // that" banner over a freshly loaded, healthy list, for the rest of the
  // project session. A ref keeps the cleanup mount-lifetime, not
  // callback-identity-lifetime.
  const dismissRef = useRef(onDismissRefusal);
  dismissRef.current = onDismissRefusal;
  useEffect(() => {
    return () => dismissRef.current();
  }, []);

  // The refusal takes the whole panel over, so it must also take focus: its
  // buttons are the only way back to the list, and without this they are
  // reachable only by tabbing from the top of the document.
  const refusalActionRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (refusal !== null) refusalActionRef.current?.focus();
  }, [refusal]);

  // All five refusals paint. A 401 now fires the app's one sign-in reaction from
  // `lib/http`, but `beforeLoad` runs on a route MATCH and a drawer click is not
  // one — so this panel is what the drawer shows if that reaction is ever
  // missing (an unregistered seam, a test harness, a mis-ordered boot). It used
  // to render nothing at all, which is how a lapsed cookie reached a user as a
  // dead row.
  if (refusal !== null) {
    const terminal = refusal !== "unavailable";
    // Four terminal conditions, four headings. A stale chat label is neither a
    // finished conversation nor a swept one: the transcript is there, it is
    // simply about an issue, and reopening one is not offered here. The fifth,
    // `signedOut`, is about the reader rather than the row.
    return (
      <div className="focus-rail-ended" role="alert">
        {/* Heading and body take the SAME three-way split. A transient failure
            under a terminal headline announces a permanent condition over a
            body that invites a retry — the contradiction this panel exists to
            remove, in the other direction. */}
        <span className="focus-rail-ended-title">
          {t(
            refusal === "gone"
              ? "focus.rail.goneTitle"
              : refusal === "revoked"
                ? "focus.rail.endedTitle"
                : refusal === "issueOnly"
                  ? "focus.rail.issueOnlyTitle"
                  : refusal === "signedOut"
                    ? "focus.rail.signedOutTitle"
                    : "focus.rail.openFailedTitle",
          )}
        </span>
        <span className="focus-rail-ended-body">
          {t(
            refusal === "gone"
              ? "focus.rail.goneBody"
              : refusal === "revoked"
                ? "focus.rail.endedBody"
                : refusal === "issueOnly"
                  ? "focus.rail.issueOnlyBody"
                  : refusal === "signedOut"
                    ? "focus.rail.signedOutBody"
                    : "focus.rail.openFailed",
          )}
        </span>
        {/* A retry is offered only where retrying can work. A revoked session
            and a swept transcript are both permanent, and inviting a second
            click on them is the lie this panel exists to stop telling. */}
        {!terminal && (
          <button
            ref={refusalActionRef}
            type="button"
            className="focus-btn"
            onClick={onRetry}
          >
            {t("focus.rail.retry")}
          </button>
        )}
        {/* The one refusal with a recovery that is not a retry. A HARD
            navigation on purpose: this button is the fail-visible path for the
            case where the registered `lib/http` seam did not fire, so it must
            not depend on it. */}
        {refusal === "signedOut" && (
          <button
            ref={refusalActionRef}
            type="button"
            className="focus-btn"
            onClick={() => {
              // The web's sign-in page; in the Focus panel its signed-out
              // notice, since signing in again happens in the assistant.
              signInAgain();
            }}
          >
            {t("focus.rail.signIn")}
          </button>
        )}
        <button
          // `signedOut` is terminal too, so without excluding it BOTH nodes
          // would claim the ref and the later commit would win — focus landing
          // on "Back to the list" rather than on the one control that recovers.
          ref={terminal && refusal !== "signedOut" ? refusalActionRef : null}
          type="button"
          className="focus-btn is-ghost"
          onClick={onDismissRefusal}
        >
          {t("focus.rail.endedDismiss")}
        </button>
      </div>
    );
  }

  /**
   * What names a row: the stored title, or the placeholder's replacement.
   *
   * The three decorations this used to carry — an `#<iid> · <title>` label from
   * `issueIid`, a `#<iid>` salvaged out of the legacy refine seed, and a
   * `lang="en"` wrap on the create seed's English opener — are gone.
   *
   * For anything the CURRENT server writes, the `issueIid` branch is dead:
   * `ws/stream.ts` computes `turnKind = "issue"` on the same flow-bearing user
   * turn that fires `onFlowBound` → `recordIssueLabel`, and `promoteKind` is
   * monotonic — so the write that stamps `issueIid` also promotes the row out of
   * this `kind=chat` list. The two hooks are `void`-fired independently, so a
   * list fetched between them can still see `chat` + `issueIid`, but only for
   * that window.
   *
   * What the branches could really fire on is LEGACY data, written before that
   * promotion existed. For those rows the refusal is NOT guaranteed: the pick's
   * `issueOnly` leg keys on the DERIVED `flowIssueIid`, and
   * `deriveFlowFromEvents` reports the MOST RECENT user turn's iid — so a row
   * whose last turn was free-form resolves `null` and opens as an ordinary chat
   * (as does any row whose event-log read throws, which the detail route
   * degrades to `null` on purpose). Losing the label on that class is accepted:
   * `issueIid` says an issue was touched at some point, not that this button
   * lands anywhere near it, and naming an issue on a button whose issue landing
   * this rail withholds is a promise it cannot keep.
   */
  const rowLabel = (entry: SessionIndexEntry): ReactNode => {
    const title = entry.title.trim();
    // Reachable, and it stays: `onUserTurnKind` (the promotion) and
    // `onUserMessage` (the rename) are two independent `void`-fired writes off
    // the same inbound message, so a list fetched between them legitimately
    // sees a promoted row still wearing the server clock.
    if (title === "" || PLACEHOLDER_TITLE.test(title)) {
      return t("focus.rail.untitled");
    }
    return title;
  };

  // Rows in server order, cut into runs of one local day each.
  const groups = groupByDay(chatSessions, (entry) => entry.lastActiveAt);
  // A new server emits `kind` on every row and honours the param, so
  // "rows came back, none of them chats" is not a heuristic — it is proof the
  // filter was dropped, i.e. the rolling-deploy window. It reads as a transient
  // mismatch with a retry, never as an empty history.
  const filterUnsupported =
    state.kind === "ready" &&
    state.sessions.length > 0 &&
    chatSessions.length === 0;
  const todayKey = dayKeyOf(new Date().toISOString());
  const yesterdayKey = dayKeyOf(
    new Date(Date.now() - 86_400_000).toISOString(),
  );
  const groupHeading = (key: string): string =>
    key === todayKey
      ? t("focus.rail.today")
      : key === yesterdayKey
        ? t("focus.rail.yesterday")
        : groupDate(`${key}T12:00:00`);

  return (
    <>
      {/* ABOVE every list state, and rendered in all of them — loading, error,
          the rolling-deploy mismatch, and an empty history, which is the one
          where it matters most: a user with no chats at all is exactly who
          needs to start one, and gating it on the fetch would hide it behind
          the failure it is unrelated to. It is deliberately NOT inside the
          refusal takeover above, which returns before this — a refusal is about
          one row, and "Zurück zur Liste" lands the reader right back here.

          Inert while a pick resolves, for the same reason the rows are: one
          gesture starts at most one session, and this one always mints. */}
      <div className="focus-rail-newchat">
        <button
          type="button"
          className="focus-btn"
          aria-disabled={pendingSessionId !== null}
          onClick={() => {
            if (pendingSessionId !== null) return;
            onNewChat();
          }}
        >
          {t("focus.chat.newChat")}
        </button>
        <span className="focus-note">{t("focus.rail.newChatCost")}</span>
      </div>

      {state.kind === "loading" && (
        <>
          <div className="focus-spinner" />
          <div className="focus-rail-empty">{t("common.loading")}</div>
        </>
      )}

      {state.kind === "error" && (
        <div className="focus-rail-error" role="alert">
          <span>{t("focus.rail.loadFailed")}</span>
          {/* The recovery exists (the fetch re-fires on every drawer open), it
              was just undiscoverable — so say it with a button rather than
              leaving the message reading as terminal. */}
          <button type="button" className="focus-btn is-ghost" onClick={reload}>
            {t("focus.rail.retry")}
          </button>
        </div>
      )}

      {filterUnsupported && (
        <div className="focus-rail-error" role="alert">
          <span>{t("focus.rail.filterUnsupported")}</span>
          <button type="button" className="focus-btn is-ghost" onClick={reload}>
            {t("focus.rail.retry")}
          </button>
        </div>
      )}

      {state.kind === "ready" && !filterUnsupported && chatSessions.length === 0 && (
        <div className="focus-rail-empty">{t("focus.rail.empty")}</div>
      )}

      {state.kind === "ready" && chatSessions.length > 0 && (
        <>
          {/* The cost, said once and said unconditionally. No signal on this
              wire survives the click: the list is a snapshot fetched at
              drawer-open with no poll and no push, so a row can go ready →
              terminated between the render and the pointer, and the boot that
              click triggers is the whole cost. Twenty identical grey lines are
              wallpaper; one sentence is read. */}
          <p className="focus-rail-cost">{t("focus.rail.costNotice")}</p>

          {/* The "opening…" text reaches assistive tech from HERE, not from the
              list: an `aria-live` region around the rows re-announces every row
              whenever the grouping changes. */}
          <p className="focus-rail-status" role="status">
            {pendingSessionId !== null ? t("focus.rail.opening") : ""}
          </p>

          <div className="focus-rail-groups">
            {/* A plain `<div>`, not a named `<section>`: a section with an
                accessible name is exposed as a landmark region, and one per
                calendar day buries the dialog's own landmark under dozens of
                entries while announcing each group twice. The `<h3>` names the
                group for heading navigation already. */}
            {groups.map((group) => (
              <div key={group.key} className="focus-rail-group">
                <h3 className="focus-rail-group-head">
                  {groupHeading(group.key)}
                </h3>
                <ul
                  className="focus-rail-list"
                  aria-busy={pendingSessionId !== null}
                >
                  {group.entries.map((entry) => {
                    const isCurrent = entry.sessionId === activeSessionId;
                    const isPending = entry.sessionId === pendingSessionId;
                    const refused = unopenableRows.get(entry.sessionId);
                    // ONE state per row, so two badges cannot contradict. A
                    // refusal is later and more specific than `entry.warm` —
                    // which is a `bridgeState === "ready"` read taken in the
                    // drawer-open snapshot — so it wins, and it subsumes:
                    // whether the workspace is hot is irrelevant on a row that
                    // cannot be opened. Deriving it is the point; an
                    // `&& !isGone` patch repairs today's pair and lets the next
                    // badge repeat the bug.
                    const rowState:
                      | "gone"
                      | "ended"
                      | "issue"
                      | "current"
                      | "warm"
                      | "cold" =
                      refused ??
                      (isCurrent ? "current" : entry.warm ? "warm" : "cold");
                    // `aria-disabled` rather than `disabled`: a `<button>` that
                    // becomes disabled while focused drops focus to `<body>`,
                    // which is exactly what the pressed row does the instant a
                    // pick starts — and the "open now" row would be skipped by
                    // Tab, so the one row that answers "where am I?" could never
                    // be read. The click is refused in the handler instead.
                    const inert =
                      isCurrent ||
                      refused !== undefined ||
                      pendingSessionId !== null;
                    return (
                      <li key={entry.sessionId}>
                        <button
                          type="button"
                          className={`focus-rail-row${isCurrent ? " is-current" : ""}`}
                          aria-disabled={inert}
                          {...(isCurrent ? { "aria-current": true as const } : {})}
                          // The absolute date rides `title=` ONLY. The row is a
                          // `<button>` whose accessible name is its own text, so
                          // an `aria-label` would REPLACE the issue label, the
                          // relative time and the badges with a bare date.
                          title={absolute(entry.lastActiveAt)}
                          onClick={() => {
                            if (inert) return;
                            onPick(entry);
                          }}
                        >
                          <span className="focus-rail-row-title">
                            {rowLabel(entry)}
                          </span>
                          <span className="focus-rail-meta">
                            <span>{relative(entry.lastActiveAt)}</span>
                            {/* A warm sandbox — `bridgeState === "ready"` —
                                which is the only signal that implies this row
                                will not pay a cold boot. Suppressed on the row
                                the user is already in, where the blue border
                                and "open now" already say it. */}
                            {rowState === "warm" && (
                              <span className="focus-rail-badge is-live">
                                {t("focus.rail.live")}
                              </span>
                            )}
                            {rowState === "current" && (
                              <span className="focus-rail-badge">
                                {t("focus.rail.current")}
                              </span>
                            )}
                            {isPending && (
                              <span aria-hidden>{t("focus.rail.opening")}</span>
                            )}
                          </span>
                          {/* The second DOM slot, off the SAME source as the
                              badges above — which is what makes "running" and
                              "no longer stored" on one row unrepresentable. */}
                          {refused !== undefined && (
                            <span className="focus-rail-row-gone">
                              {t(
                                rowState === "gone"
                                  ? "focus.rail.rowGone"
                                  : rowState === "ended"
                                    ? "focus.rail.rowEnded"
                                    : "focus.rail.rowIssue",
                              )}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          {/* Exactly at the ceiling, so the list says what it is not showing
              rather than stopping silently. */}
          {chatSessions.length === RAIL_LIMIT && (
            <p className="focus-rail-capped">
              {t("focus.rail.capped", { count: RAIL_LIMIT })}
            </p>
          )}
        </>
      )}
    </>
  );
}
